import i18n from '@/i18n/config';
import type { Lead, LeadContactType } from '@/types';

import { useDisclosure } from '@heroui/react';
import { useState, useCallback } from 'react';
import { useAtom } from 'jotai';
import { toast } from 'react-toastify';

import { createLeadContact, deleteLeadContact } from '@/store';

export function useLeadContacts() {
  const [, createContact] = useAtom(createLeadContact);
  const [, deleteContact] = useAtom(deleteLeadContact);
  const { isOpen: isAddModalOpen, onOpen: onAddModalOpen, onOpenChange } = useDisclosure();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingContactId, setDeletingContactId] = useState<number | null>(null);

  const onAddModalOpenChange = useCallback(() => {
    onOpenChange();
    setSelectedLead(null);
  }, [onOpenChange]);

  const openAddModal = useCallback(
    (lead: Lead) => {
      setSelectedLead(lead);
      onAddModalOpen();
    },
    [onAddModalOpen]
  );

  const handleCreateContact = useCallback(
    async (type: LeadContactType, value: string, source?: string) => {
      if (!selectedLead) return;

      try {
        setIsSubmitting(true);
        await createContact({ leadId: selectedLead.id, type, value, source });
        toast.success(i18n.t('copy.contact_added_successfully'));
        onAddModalOpenChange();
        setSelectedLead(null);
      } catch (error) {
        // handled globally by axios interceptor
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedLead, createContact, onAddModalOpenChange]
  );

  const handleDeleteContact = useCallback(
    async (leadId: number, contactId: number) => {
      try {
        setDeletingContactId(contactId);
        await deleteContact({ leadId, contactId });
        toast.success(i18n.t('copy.contact_deleted_successfully'));
      } catch (error) {
        // handled globally
      } finally {
        setDeletingContactId(null);
      }
    },
    [deleteContact]
  );

  return {
    isAddModalOpen,
    selectedLead,
    isSubmitting,
    deletingContactId,
    openAddModal,
    handleCreateContact,
    handleDeleteContact,
    onAddModalOpenChange
  };
}

export default useLeadContacts;

